import { toast } from "react-toastify";
import { authApi, getAuthorizationHeader } from "../api-calls/user-requests";
import { CREATE_SUBSCRIPTION } from "../api-calls/api-urls";
import loadRazorpayScript from "../utilities/loadRazorpay";

const RAZORPAY_CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/**
 * Shared Razorpay recurring-subscription checkout plumbing, extracted out of
 * the 5 near-identical one-time-payment implementations that used to live in
 * SubscriptionsHome/OrderSummary/OrderSummaryForAll/CreateTeam/registeredSuccessfully.
 *
 * Only the Razorpay script-load + create-subscription + checkout-open/dismiss
 * boilerplate is de-duplicated here — each caller keeps its own distinct
 * post-authorization chain (select/payment-success/renewSubscription order
 * differs per flow), passed in as onAuthorized.
 */
export function useRazorpaySubscriptionCheckout() {
  const startRecurringCheckout = async ({
    subscriptionId,
    durationId,
    customerSubId,
    description,
    onAuthorized,
    onCancelled,
    onFailed,
  }) => {
    const scriptLoaded = await loadRazorpayScript(RAZORPAY_CHECKOUT_SRC);
    if (!scriptLoaded) {
      toast.error("Razorpay SDK failed to load. Are you online?");
      return;
    }

    const { data } = await authApi.post(
      CREATE_SUBSCRIPTION,
      {
        subscriptionid: subscriptionId,
        durationid: durationId,
        customer_sub_id: customerSubId,
      },
      { headers: { Authorization: getAuthorizationHeader() } },
    );

    const razorpaySubscriptionId = data?.razorpay_subscription_id;
    if (!razorpaySubscriptionId) {
      toast.error("This plan isn't set up for recurring billing yet.");
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      subscription_id: razorpaySubscriptionId,
      recurring: true,
      name: "Team Player App",
      description: description || "Subscription",
      // Activation is confirmed by the backend webhook, not this callback —
      // a successful handler firing only means the auto-debit mandate was
      // authorized. onAuthorized should proceed with the caller's own
      // select/renew chain, same as it did after the old verify-order call.
      handler: async (response) => {
        await onAuthorized?.(response, data);
      },
      theme: { color: "#528FF0" },
      modal: {
        ondismiss: () => {
          toast.error("Payment Cancelled!");
          onCancelled?.();
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (response) => {
      console.error("Payment failed:", response);
      alert("Payment Failed!\nReason: " + response.error.description);
      onFailed?.(response);
    });
    rzp.open();
  };

  return { startRecurringCheckout };
}

export default useRazorpaySubscriptionCheckout;
